import { Emitter } from "@core/events/emitter";
import type { EntityTransform } from "@models/data/entityData";
import type { IProjectModel } from "@models/project/IProjectModel";
import type { IUndoRedo } from "@models/project/IUndoRedo";
import type { IGrabModifierModel } from "./IGrabModifierModel";

/** Unity `GrabModifierModel.m_snapThreshold` default. */
export const K_GRAB_SNAP_THRESHOLD_DEFAULT = 0.1;

/**
 * `GrabModifierModel` — headless web port of the desktop-relevant state of
 * Unity `Models/Modifiers/GrabModifierModel.cs` (+ the option plumbing from
 * `GrabTool` / `DesktopGrabToolViewModel`). See `IGrabModifierModel` for the
 * ownership split and the documented desktop-scope deviations.
 */
export class GrabModifierModel implements IGrabModifierModel {
  readonly onOptionsChanged = new Emitter<void>();
  readonly onGrabStateChanged = new Emitter<{
    isGrabbing: boolean;
    snapshotId: string | null;
  }>();

  private m_usePartSelection = true;
  private m_isSnapMode = true;
  private m_snapThreshold = K_GRAB_SNAP_THRESHOLD_DEFAULT;
  private m_grabbedSnapshotId: string | null = null;
  /**
   * Snapshots that went through a grab this app SESSION (local or remote) —
   * Unity `m_entityOriginalPositions`' key set. Scene-scoped like Unity's
   * targets (`ClearTargets` on scene change); never persisted.
   */
  private readonly m_grabbedThisSession = new Set<string>();

  constructor(
    private readonly m_project: IProjectModel,
    private readonly m_undoRedo: IUndoRedo,
  ) {
    // Snap mode mirrors the ACTIVE scene's persisted `ShouldSnapback`
    // (DesktopGrabToolViewModel re-reads it on enable; the web re-syncs on
    // every scene switch so the toggle always shows the scene's truth).
    this.m_project.onActiveSceneChanged.on((scene) => {
      const next = scene?.shouldSnapback ?? true;
      if (this.m_isSnapMode !== next) {
        this.m_isSnapMode = next;
        this.onOptionsChanged.emit();
      }
      // A scene switch mid-grab drops the session (targets are scene-scoped,
      // Unity `ClearTargets` — including the modified-object bookkeeping).
      this.m_grabbedThisSession.clear();
      this.notifyGrabEnded();
    });
  }

  get usePartSelection(): boolean {
    return this.m_usePartSelection;
  }

  get isSnapMode(): boolean {
    return this.m_isSnapMode;
  }

  get snapThreshold(): number {
    return this.m_snapThreshold;
  }

  get isGrabbing(): boolean {
    return this.m_grabbedSnapshotId !== null;
  }

  get grabbedSnapshotId(): string | null {
    return this.m_grabbedSnapshotId;
  }

  setUsePartSelection(value: boolean): void {
    if (this.m_usePartSelection === value) return;
    this.m_usePartSelection = value;
    this.onOptionsChanged.emit();
  }

  setSnapMode(value: boolean): void {
    if (this.m_isSnapMode === value) return;
    this.m_isSnapMode = value;
    this.onOptionsChanged.emit();
    const sceneId = this.m_project.activeSceneId;
    if (sceneId) {
      void this.m_project
        .updateSceneShouldSnapbackAsync(sceneId, value)
        .catch((err) => console.warn("[GrabModifierModel] snapback persist failed:", err));
    }
  }

  setSnapThreshold(value: number): void {
    const clamped = Math.min(1, Math.max(0, value));
    if (this.m_snapThreshold === clamped) return;
    this.m_snapThreshold = clamped;
    this.onOptionsChanged.emit();
  }

  notifyGrabStarted(snapshotId: string): void {
    this.noteGrabTarget(snapshotId);
    if (this.m_grabbedSnapshotId === snapshotId) return;
    this.m_grabbedSnapshotId = snapshotId;
    this.onGrabStateChanged.emit({ isGrabbing: true, snapshotId });
  }

  noteGrabTarget(snapshotId: string): void {
    if (!snapshotId) return;
    this.m_grabbedThisSession.add(snapshotId);
  }

  /**
   * Unity `GrabModifierModel.GetModifiedObjects`: of the entities that went
   * through a grab THIS app session (local or remote), the ones whose current
   * local position differs from the hard-reset/library pose. Quirks kept
   * verbatim: POSITION ONLY (a rotation-only grab is never re-broadcast) and
   * EXACT inequality (no epsilon).
   */
  getModifiedSnapshotIds(): ReadonlyArray<string> {
    const sceneId = this.m_project.activeSceneId;
    if (!sceneId) return [];
    const modified: string[] = [];
    for (const snapshotId of this.m_grabbedThisSession) {
      const snapshot = this.m_project.getEntitySnapshotById(sceneId, snapshotId);
      if (!snapshot) continue;
      const current = snapshot.transform.position;
      const original = snapshot.resetTransform.position;
      if (
        current[0] !== original[0]
        || current[1] !== original[1]
        || current[2] !== original[2]
      ) {
        modified.push(snapshotId);
      }
    }
    return modified;
  }

  notifyGrabEnded(): void {
    if (this.m_grabbedSnapshotId === null) return;
    this.m_grabbedSnapshotId = null;
    this.onGrabStateChanged.emit({ isGrabbing: false, snapshotId: null });
  }

  commitGrab(
    sceneId: string,
    snapshotId: string,
    previousTransform: EntityTransform,
    nextTransform: EntityTransform,
  ): void {
    // `record` executes redo immediately — that first run IS the grab's
    // persistence commit (Unity `UndoRedo.Record` semantics, UndoRedo.cs:151).
    this.m_undoRedo.record({
      label: "Grab",
      redo: () => {
        void this.m_project
          .updateEntitySnapshotAsync(
            sceneId,
            snapshotId,
            { transform: nextTransform },
            undefined,
            { grab: true },
          )
          .catch((err) => console.warn("[GrabModifierModel] grab persist failed:", err));
      },
      undo: () => {
        void this.m_project
          .updateEntitySnapshotAsync(
            sceneId,
            snapshotId,
            { transform: previousTransform },
            undefined,
            { grab: true },
          )
          .catch((err) => console.warn("[GrabModifierModel] grab undo failed:", err));
      },
    });
  }

  async resetSceneAsync(): Promise<void> {
    // Unity: GrabTool.ResetAll → GrabModifierModel.ResetAll (authored-pose
    // restore + bulk persist) + delete scene collision annotation. The web
    // routes through the shared scene reset (materials untouched) — see the
    // interface header for the documented deviation.
    await this.m_project.resetActiveSceneAsync(false);
  }
}
